import { doc, setDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import { readStoredCloudMevEnv } from "../cloud-mev/cloudMevEnv";

const DEVICE_ID = /^[0-9A-Za-z]{4}-[0-9A-Za-z]{4}-[0-9A-Za-z]{4}$/;
const PLACEHOLDER = "0000-0000-0000";
const published = new Set();

export function isIssuedDeviceId(value) {
  const id = String(value || "").trim();
  return DEVICE_ID.test(id) && id !== PLACEHOLDER;
}

function deviceIdFromName(name) {
  const match = String(name || "").match(/^MEV_(?:Key|Terminal)_([0-9A-Za-z]{4}-[0-9A-Za-z]{4}-[0-9A-Za-z]{4})_/i);
  return isIssuedDeviceId(match?.[1]) ? match[1] : "";
}

function preferredEnvs(storeId) {
  const envs = [];
  const stored = readStoredCloudMevEnv(storeId);
  if (stored) envs.push(stored);
  try {
    const legacy = String(localStorage.getItem("envirn") || "").trim().toUpperCase();
    if (legacy) envs.push(legacy);
  } catch {
    // No saved environment.
  }
  ["DEV", "ESSAI", "PROD"].forEach((env) => {
    if (!envs.includes(env)) envs.push(env);
  });
  return envs;
}

function terminalLists(storeId) {
  if (!storeId || typeof localStorage === "undefined") return [];
  const lists = [];
  preferredEnvs(storeId).forEach((env) => {
    try {
      const raw = localStorage.getItem(`cloudMevLocal_terminals_${storeId}_${env}`);
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed?.items)) lists.push(...parsed.items);
    } catch {
      // Skip a list this PC cannot read.
    }
  });
  return lists;
}

async function deviceIdFromThumbprint(thumbprint) {
  const api = typeof window !== "undefined" ? window.api : null;
  if (!api?.invoke || !thumbprint) return "";
  const info = await api.invoke("verify-certificate-exists", thumbprint).catch(() => null);
  return deviceIdFromName(info?.FriendlyName || info?.friendlyName || "");
}

export async function readWorkstationDeviceId(storeId) {
  if (!storeId) return "";
  const seen = new Set();
  const thumbprints = [];
  const add = (value) => {
    const thumbprint = String(value || "").trim();
    if (!thumbprint || seen.has(thumbprint)) return;
    seen.add(thumbprint);
    thumbprints.push(thumbprint);
  };
  preferredEnvs(storeId).forEach((env) => {
    try {
      add(localStorage.getItem(`mev_current_thumbprint_${storeId}_${env.toLowerCase()}`));
    } catch {
      // No thumbprint for this environment.
    }
  });
  terminalLists(storeId).forEach((item) => add(item?.thumbprint));
  for (const thumbprint of thumbprints) {
    const deviceId = await deviceIdFromThumbprint(thumbprint);
    if (deviceId) return deviceId;
  }
  const listed = terminalLists(storeId).find((item) => isIssuedDeviceId(item?.deviceId));
  return listed ? String(listed.deviceId) : "";
}

export async function publishStationDevice(storeId, stationId, deviceId) {
  if (!storeId || !stationId || !isIssuedDeviceId(deviceId)) return;
  const key = `${storeId}|${stationId}|${deviceId}`;
  if (published.has(key)) return;
  await setDoc(doc(db, "stores", storeId, "stations", stationId), {
    deviceId,
    deviceIdSetAt: Date.now(),
  }, { merge: true });
  published.add(key);
}
